import RAPIER from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { AudioEngine } from '../audio/AudioEngine';
import { ChaseCamera } from '../camera/ChaseCamera';
import { CART, CartView } from '../cart/CartModel';
import { Vehicle } from '../cart/Vehicle';
import { configuredAdminIds, isAdminCredential } from '../config/admin';
import { ADMIN_STORAGE_KEY, defaultGameConfig, type GameConfigDocument, type PublicMatchMode } from '../config/contract';
import { createGameConfigClient } from '../config/createGameConfig';
import type { GameConfigClient } from '../config/GameConfigClient';
import { Input, type DriveInput } from '../input/Input';
import { createProgressRepository } from '../progress/createProgressRepository';
import type { FinishUpdate } from '../progress/ProgressService';
import { ProgressService } from '../progress/ProgressService';
import type { PhaseId } from '../progress/model';
import { BOOST_DRAIN_PER_SEC, BOOST_MAX, BOOST_START } from '../race/boost';
import { OpponentField } from '../race/Opponents';
import { RaceSession } from '../race/RaceSession';
import { lockReason, nextPlayable, phaseById, PHASES, isUnlocked } from '../tracks/phases';
import { surfaceAlong } from '../tracks/RoadBuilder';
import { disposeObject } from '../render/dispose';
import { nearestFrame, projectSpline, sampleSpline } from '../tracks/spline';
import type { BuiltTrack, ColliderMeta, TrackContext } from '../tracks/types';
import { Ui } from '../ui/Ui';
import { clamp, formatSpeed } from '../util/format';

const FIXED = 1 / 60;
const ZERO_INPUT: DriveInput = { throttle: 0, brake: 0, steer: 0, handbrake: false, boost: false };

export class Game {
  private readonly input = new Input();
  private readonly audio = new AudioEngine();
  private readonly ui: Ui;
  private progress!: ProgressService;
  private renderer!: THREE.WebGLRenderer;
  private menuScene!: THREE.Scene;
  private raceScene!: THREE.Scene;
  private menuCamera!: THREE.PerspectiveCamera;
  private chase!: ChaseCamera;
  private menuCart!: CartView;
  private world!: RAPIER.World;
  private staticBody!: RAPIER.RigidBody;
  private readonly meta = new Map<number, ColliderMeta>();
  private trackColliders: RAPIER.Collider[] = [];
  private track: BuiltTrack | null = null;
  private player: Vehicle | null = null;
  private opponents: OpponentField | null = null;
  private race: RaceSession | null = null;
  private phaseId: PhaseId | null = null;
  private sun!: THREE.DirectionalLight;
  private hemi!: THREE.HemisphereLight;
  private mode: 'menu' | 'race' = 'menu';
  private paused = false;
  private busy = false;
  private finishLock = false;
  private launching = false;
  private launchTimer = 0;
  private launchElapsed = 0;
  private launchBeepStep = -1;
  private launchUpdate: FinishUpdate | null = null;
  private launchTotal: number | undefined;
  private launchMessage = '';
  private motes: THREE.Points | null = null;
  private moteSpeeds: Float32Array | null = null;
  private lastFrame = 0;
  private helpTimer = 0;
  private collisionCooldown = 0;
  private nudgeCooldown = 0;
  private colliderDebug: THREE.Group | null = null;
  private scriptedDrive = false;
  private started = false;
  private frames = 0;
  private softwareRenderer = false;
  private webgl = false;
  private fatalMessage: string | null = null;
  private gameConfigClient!: GameConfigClient;
  private gameConfig: GameConfigDocument = defaultGameConfig();
  private adminUnlocked = false;
  private adminId = 'local-admin';
  private boost = BOOST_START;
  private boostWas = false;
  constructor(
    private readonly canvas: HTMLCanvasElement,
    root: HTMLElement,
  ) {
    this.ui = new Ui(root, {
      onPlay: () => void this.startPhase(nextPlayable((id) => this.progress.isCompleted(id))),
      onPhases: () => this.ui.push('phases'),
      onGarage: () => this.ui.push('garage'),
      onSettings: () => this.ui.push('settings'),
      onInstructions: () => this.ui.push('instructions'),
      onBack: () => this.ui.back(),
      onSelectPhase: (id) => void this.startPhase(id),
      onResume: () => this.setPaused(false),
      onRestart: () => {
        if (this.phaseId) void this.startPhase(this.phaseId);
      },
      onQuit: () => this.leaveRace(),
      onChangeSettings: (partial) => void this.applySettings(partial),
      onResetProgress: () => void this.resetProgress(),
      onReplay: () => {
        if (this.phaseId) void this.startPhase(this.phaseId);
      },
      onUnlockAdmin: (code) => this.unlockAdmin(code),
      onSetMatchMode: (mode) => void this.setPublicMatchMode(mode),
    }, (button, control) => this.bindTouch(button, control));
  }

  private graphics(): 'low' | 'high' {
    if (this.softwareRenderer) return 'low';
    return this.progress?.settings.graphics ?? 'high';
  }

  async start(): Promise<void> {
    if (!this.detectWebGL()) {
      this.webgl = false;
      this.fatal('Este jogo precisa de WebGL. Atualize o navegador ou ative a aceleração gráfica.');
      this.publishDebug();
      return;
    }
    this.webgl = true;
    this.ui.showLoading('Preparando o carrinho…');
    const repo = createProgressRepository();
    const profile = await repo.load();
    this.progress = new ProgressService(repo, profile);
    this.ui.setProfile(profile, repo.kind, false);
    this.gameConfigClient = createGameConfigClient();
    this.gameConfig = await this.gameConfigClient.load();
    this.restoreAdmin();
    this.ui.setMatchAdmin(this.adminUnlocked, this.gameConfig.publicMatch.mode);

    await RAPIER.init();
    this.world = new RAPIER.World({ x: 0, y: -12, z: 0 });
    this.world.timestep = FIXED;
    this.world.lengthUnit = 1;
    this.world.integrationParameters.numSolverIterations = 8;
    this.staticBody = this.world.createRigidBody(RAPIER.RigidBodyDesc.fixed());

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    const gl = this.renderer.getContext();
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    if (info) {
      const name = String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL));
      this.softwareRenderer = /swiftshader|llvmpipe|softpipe|software/i.test(name);
    }
    this.canvas.addEventListener('webglcontextlost', (event) => {
      event.preventDefault();
      this.fatal('O contexto WebGL foi perdido. Recarregue a página.');
    });

    const pmrem = new THREE.PMREMGenerator(this.renderer);
    const menuEnv = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    this.menuScene = new THREE.Scene();
    this.menuScene.environment = menuEnv;
    this.menuScene.background = new THREE.Color(0x14180f);
    this.raceScene = new THREE.Scene();
    this.raceScene.environment = menuEnv;

    this.menuCamera = new THREE.PerspectiveCamera(38, 1, 0.1, 40);
    this.chase = new ChaseCamera(1);
    this.buildStudio();
    this.buildRaceLights();
    this.applyGraphics();
    this.resize();
    window.addEventListener('resize', () => this.resize());
    window.addEventListener('pointerdown', () => this.audio.unlock(), { passive: true });
    window.addEventListener('keydown', () => this.audio.unlock());

    this.mode = 'menu';
    this.ui.show('title');
    this.ui.setHudVisible(false);
    this.lastFrame = performance.now();
    this.started = true;
    this.publishDebug();
    requestAnimationFrame(this.frame);
  }

  private detectWebGL(): boolean {
    try {
      const probe = document.createElement('canvas');
      return Boolean(probe.getContext('webgl2') || probe.getContext('webgl'));
    } catch {
      return false;
    }
  }

  private fatal(message: string): void {
    this.fatalMessage = message;
    const root = this.canvas.parentElement?.querySelector('#ui');
    if (root) {
      root.innerHTML = `<section class="panel fatal"><h1>Supermarket Kart Racing</h1><p>${message}</p></section>`;
    }
  }

  private buildStudio(): void {
    this.hemi = new THREE.HemisphereLight(0xfff6e4, 0x241910, 0.85);
    const key = new THREE.DirectionalLight(0xfff3dd, 2.4);
    key.position.set(4, 6, 3);
    key.castShadow = true;
    const rim = new THREE.DirectionalLight(0xff3b30, 0.85);
    rim.position.set(-4, 3, -2);
    this.menuScene.add(this.hemi, key, rim);
    const floor = new THREE.Mesh(
      new THREE.CircleGeometry(5, 48),
      new THREE.MeshStandardMaterial({ color: 0x191d17, metalness: 0.72, roughness: 0.28 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(1.7, 0.018, 8, 64),
      new THREE.MeshStandardMaterial({ color: 0xf5d90a, emissive: 0xf5d90a, emissiveIntensity: 0.45, roughness: 0.4 }),
    );
    ring.rotation.x = Math.PI / 2;
    ring.position.y = 0.012;
    this.menuScene.add(floor, ring);
    this.menuCart = new CartView({ plate: '1', shadows: true });
    this.menuCart.root.position.set(1.2, CART.rideHeight, 0);
    this.menuScene.add(this.menuCart.root);
  }

  private buildRaceLights(): void {
    this.hemi = new THREE.HemisphereLight(0xc5def5, 0x6a6258, 0.72);
    this.sun = new THREE.DirectionalLight(0xfff1d6, 2.3);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(1024, 1024);
    this.sun.shadow.bias = -0.0012;
    this.sun.shadow.normalBias = 0.04;
    const shadow = this.sun.shadow.camera;
    shadow.near = 1;
    shadow.far = 70;
    shadow.left = -22;
    shadow.right = 22;
    shadow.top = 22;
    shadow.bottom = -22;
    this.raceScene.add(this.hemi, this.sun, this.sun.target);
  }

  private resize(): void {
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    const ratioCap = this.softwareRenderer ? 0.55 : this.graphics() === 'low' ? 1 : 1.5;
    this.renderer?.setPixelRatio(Math.min(window.devicePixelRatio || 1, ratioCap));
    this.renderer?.setSize(width, height, false);
    this.menuCamera.aspect = width / Math.max(1, height);
    this.menuCamera.updateProjectionMatrix();
    this.chase?.setAspect(width / Math.max(1, height));
    if (this.mode === 'race') this.ui.setHudVisible(!this.paused ? true : true);
  }

  private async applySettings(partial: Partial<typeof this.progress.settings>): Promise<void> {
    await this.progress.updateSettings(partial);
    this.audio.setVolume(this.progress.settings.volume);
    this.chase.mode = this.progress.settings.camera;
    this.chase.shakeEnabled = this.progress.settings.cameraShake;
    this.applyGraphics();
    this.ui.setProfile(this.progress.data, this.progress.kind, this.progress.degraded);
  }

  private applyGraphics(): void {
    if (!this.renderer) return;
    const high = this.graphics() === 'high';
    this.renderer.shadowMap.enabled = high;
    this.sun.castShadow = high;
    this.renderer.toneMappingExposure = this.track?.atmosphere.exposure ?? 1.05;
    this.resize();
  }

  private async resetProgress(): Promise<void> {
    await this.progress.reset();
    this.ui.setProfile(this.progress.data, this.progress.kind, this.progress.degraded);
    this.ui.show('settings');
  }

  private bindTouch(button: HTMLElement, control: 'throttle' | 'brake' | 'left' | 'right' | 'handbrake' | 'boost'): void {
    const down = (event: Event) => {
      event.preventDefault();
      this.audio.unlock();
      this.input.setVirtual(control, true);
    };
    const up = (event: Event) => {
      event.preventDefault();
      this.input.setVirtual(control, false);
    };
    button.addEventListener('pointerdown', down);
    button.addEventListener('pointerup', up);
    button.addEventListener('pointercancel', up);
    button.addEventListener('pointerleave', up);
  }

  private context(): TrackContext {
    return {
      rapier: RAPIER,
      world: this.world,
      staticBody: this.staticBody,
      meta: this.meta,
      quality: this.graphics(),
      addCollider: (desc, meta) => {
        const collider = this.world.createCollider(desc, this.staticBody);
        this.meta.set(collider.handle, meta);
        this.trackColliders.push(collider);
        return collider;
      },
    };
  }

  private clearTrackColliders(): void {
    for (const collider of this.trackColliders) {
      this.meta.delete(collider.handle);
      this.world.removeCollider(collider, true);
    }
    this.trackColliders = [];
  }

  private leaveRace(): void {
    this.disposeRace();
    this.mode = 'menu';
    this.paused = false;
    this.ui.show('title');
    this.ui.setHudVisible(false);
    this.audio.silence();
  }

  private disposeRace(): void {
    this.opponents?.dispose();
    this.opponents = null;
    this.player?.dispose();
    this.player = null;
    if (this.track) {
      this.raceScene.remove(this.track.object);
      this.track.dispose();
      this.track = null;
    }
    this.clearTrackColliders();
    this.clearColliderDebug();
    this.clearMotes();
    this.audio.setAmbience('none');
    this.launching = false;
    this.launchUpdate = null;
    this.race = null;
    this.finishLock = false;
  }

  async startPhase(id: PhaseId): Promise<void> {
    if (!this.started || this.busy) return;
    const phase = phaseById(id);
    const reason = lockReason(phase, (phaseId) => this.progress.isCompleted(phaseId));
    if (reason || !phase.load) {
      this.ui.show('phases');
      return;
    }
    this.busy = true;
    this.paused = false;
    this.ui.showLoading(`Carregando ${phase.name}…`);
    this.ui.setHudVisible(false);
    try {
      this.disposeRace();
      const factory = await phase.load();
      const track = factory.create(this.context());
      this.track = track;
      this.phaseId = id;
      this.raceScene.add(track.object);
      this.applyAtmosphere(track);
      this.audio.setAmbience(track.ambience ?? 'none');
      this.spawnMotes();
      const playerView = new CartView({
        plate: '1',
        propulsion: track.propulsion,
        shadows: this.graphics() === 'high',
      });
      this.raceScene.add(playerView.root);
      this.player = new Vehicle(
        RAPIER,
        this.world,
        this.meta,
        playerView,
        track.spawn.position,
        track.spawn.quaternion,
        track.surfaceId,
      );
      const spawnDistance = 8;
      this.player.splineHint = this.frameIndexAt(spawnDistance);
      this.player.splineDistance = spawnDistance;
      const aiViews = ['2', '3', '4'].map((plate) => new CartView({
        plate,
        propulsion: track.propulsion,
        shadows: false,
      }));
      for (const view of aiViews) this.raceScene.add(view.root);
      this.opponents = new OpponentField(RAPIER, this.world, this.meta, aiViews, track.frames, track.lapLength);
      this.race = new RaceSession(phase.laps, track.lapLength, track.checkpoints, this.gameConfig.publicMatch.mode);
      this.boost = BOOST_START;
      this.boostWas = false;
      this.chase.snap(track.spawn.position, track.spawn.quaternion);
      this.chase.mode = this.progress.settings.camera;
      this.chase.shakeEnabled = this.progress.settings.cameraShake;
      this.helpTimer = 0;
      this.mode = 'race';
      this.ui.show('boot');
      this.ui.setHudVisible(true);
      await this.progress.recordAttempt(id);
      this.ui.setProfile(this.progress.data, this.progress.kind, this.progress.degraded);
    } catch (error) {
      console.error(error);
      this.leaveRace();
      this.ui.show('title');
    } finally {
      this.busy = false;
    }
  }

  private applyAtmosphere(track: BuiltTrack): void {
    const atmosphere = track.atmosphere;
    this.raceScene.background = new THREE.Color(atmosphere.background);
    this.raceScene.fog = atmosphere.fogColor == null
      ? null
      : new THREE.Fog(atmosphere.fogColor, atmosphere.fogNear, atmosphere.fogFar);
    this.hemi.color.set(atmosphere.hemiSky);
    this.hemi.groundColor.set(atmosphere.hemiGround);
    this.hemi.intensity = atmosphere.hemiIntensity;
    this.sun.color.set(atmosphere.sunColor);
    this.sun.intensity = atmosphere.sunIntensity;
    this.renderer.toneMappingExposure = atmosphere.exposure;
  }

  private setPaused(paused: boolean): void {
    if (this.mode !== 'race' || !this.race || this.race.status === 'finished') return;
    this.paused = paused;
    if (paused) {
      this.input.releaseAll();
      this.ui.push('paused');
      this.audio.silence();
    } else {
      this.ui.show('boot');
      this.ui.setHudVisible(true);
    }
  }

  private respawn(): void {
    if (!this.player || !this.track || !this.race) return;
    const marker = this.track.checkpoints[this.race.lastSafeIndex] ?? this.track.checkpoints[0];
    const position = marker?.position ?? this.track.spawn.position;
    const quaternion = marker?.quaternion ?? this.track.spawn.quaternion;
    this.player.teleport(position, quaternion);
    const distance = marker?.distance ?? nearestFrame(this.track.frames, position, this.player.splineHint).distance;
    this.player.splineHint = this.frameIndexAt(distance);
    this.player.splineDistance = distance;
    this.race.noteRespawn();
    this.chase.snap(position, quaternion);
  }

  private frame = (now: number): void => {
    try {
      this.tick(now);
    } catch (error) {
      console.error('Falha no quadro do jogo', error);
    }
    requestAnimationFrame(this.frame);
  };

  private tick(now: number): void {
    this.frames += 1;
    const dt = Math.min(0.05, (now - this.lastFrame) / 1000);
    this.lastFrame = now;
    this.input.pollGamepad();
    this.handleMenuKeys();

    if (this.mode === 'race' && this.player && this.track && this.race && !this.paused) {
      const raw = this.race.status === 'racing' ? this.input.drive() : ZERO_INPUT;
      const stepDt = clamp(dt, 1 / 120, 1 / 30);
      this.world.timestep = stepDt;
      const live = this.driveForFrame(raw, stepDt);
      this.fixedStep(stepDt, live);
      const alpha = 1;
      const speed = Math.abs(this.player.telemetry.forwardSpeed);
      const steerVisual = live.steer * THREE.MathUtils.lerp(0.58, 0.22, clamp(speed / 17, 0, 1));
      const rough = this.player.probe.surface.dust;
      this.player.render(
        alpha,
        dt,
        steerVisual,
        live.brake > 0.2 || live.handbrake,
        rough,
        this.progress.settings.frontBounce,
        live.boost,
      );
      if (this.launching) this.updateLaunch(dt);
      else {
        this.chase.update(
          dt,
          this.player.view.root.position,
          this.player.view.root.quaternion,
          this.player.telemetry.forwardSpeed,
          this.graphics() === 'high' ? this.track.blockers : null,
        );
      }
      this.followSun(this.launching && this.track.finaleFocus ? this.track.finaleFocus : this.player.view.root.position);
      this.followSky();
      this.track.tick?.(now / 1000);
      this.audio.tickAmbience(dt);
      this.updateMotes(speed, dt);
      this.audio.updateDrive(
        this.player.telemetry.forwardSpeed,
        Math.abs(this.player.telemetry.lateralSpeed) * (live.handbrake ? 1.4 : 0.6),
        this.player.probe.onTrack ? 1 : 0.7,
      );
      this.helpTimer += dt;
      this.updateHud(speed);
      if (this.race.status === 'finished') void this.finishIfNeeded();
    } else if (this.mode === 'menu') {
      this.menuCart.update(dt, 0, 0, 0, false, false);
      this.menuCart.root.rotation.y += dt * 0.35;
      this.frameMenuCamera();
      this.renderer.render(this.menuScene, this.menuCamera);
    } else if (this.mode === 'race' && this.player && this.track) {
      this.renderer.render(this.raceScene, this.chase.camera);
    }

    if (this.mode === 'race' && this.player && this.track && !this.paused) {
      this.renderer.render(this.raceScene, this.chase.camera);
    }
  }

  private fixedStep(dt: number, input: DriveInput): void {
    const player = this.player;
    const track = this.track;
    const race = this.race;
    if (!player || !track || !race) return;

    player.capturePose();
    const beforePin = projectSpline(track.frames, player.currentPosition, player.splineHint, track.lapLength);
    player.splineHint = beforePin.index;
    player.splineDistance = beforePin.distance;
    const pinned = Math.abs(this.playerLateral()) < track.width * 0.55;
    if (pinned) player.pinToRoad(beforePin.y + CART.rideHeight);
    else player.releaseRoad();
    const zone = surfaceAlong(player.splineDistance, track.lapLength, track.surfaceId, track.bands);
    player.step(input, dt, zone, pinned);
    if (pinned) player.pinToRoad(beforePin.y + CART.rideHeight);
    if (!this.scriptedDrive) this.opponents?.update(
      dt,
      track.frames,
      track.lapLength,
      race.rankingDistance(player.splineDistance),
      race.status === 'racing',
      { x: player.currentPosition.x, z: player.currentPosition.z },
    );
    this.world.step();
    const translation = player.body.translation();
    const point = new THREE.Vector3(translation.x, translation.y, translation.z);
    const afterPin = projectSpline(track.frames, point, player.splineHint, track.lapLength);
    player.splineHint = afterPin.index;
    player.splineDistance = afterPin.distance;
    if (Math.abs(this.playerLateralAt(point)) < track.width * 0.55) player.pinToRoad(afterPin.y + CART.rideHeight);
    player.capturePose();

    const overlaps = new Set<number>();
    const at = player.currentPosition;
    for (const checkpoint of track.checkpoints) {
      const dx = at.x - checkpoint.position.x;
      const dy = at.y - checkpoint.position.y;
      const dz = at.z - checkpoint.position.z;
      // The sensor graph drops a cart that rests on a tilted pad. The pad mesh
      // sits on the spline, so being on that point is the pickup.
      const onPad = dx * dx + dy * dy + dz * dz < 2.8 * 2.8;
      if (onPad || this.world.intersectionPair(player.collider, checkpoint.collider)) overlaps.add(checkpoint.index);
    }
    const onCourse = Math.abs(this.playerLateral()) < track.width * 0.5 + 1.8 && translation.y > -1.5;
    const outcome = race.update(dt, overlaps, player.splineDistance, onCourse, translation.y);
    this.collectBoost(race);
    this.paintPads();
    const beep = race.consumeBeep();
    if (beep === 'go') this.audio.beep(880, 0.22, 0.14);
    else if (beep) this.audio.beep(660, 0.1, 0.1);

    const lapMs = race.consumeLap();
    if (lapMs != null && this.phaseId) void this.progress.recordLap(this.phaseId, lapMs).then(() => {
      this.ui.setProfile(this.progress.data, this.progress.kind, this.progress.degraded);
    });

    if (outcome === 'respawn') this.respawn();

    this.collisionCooldown -= dt;
    this.nudgeCooldown -= dt;
    const before = Math.hypot(player.body.linvel().x, player.body.linvel().z);
    if (!this.scriptedDrive && this.opponents && this.nudgeCooldown <= 0) {
      const rotation = player.body.rotation();
      const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(
        new THREE.Quaternion(rotation.x, rotation.y, rotation.z, rotation.w),
      );
      const forwardLen = Math.hypot(forward.x, forward.z) || 1;
      const fx = forward.x / forwardLen;
      const fz = forward.z / forwardLen;
      for (const racer of this.opponents.racers) {
        if (!this.world.intersectionPair(player.collider, racer.collider)) continue;
        const ai = racer.body.translation();
        let dx = translation.x - ai.x;
        let dz = translation.z - ai.z;
        const along = dx * fx + dz * fz;
        if (along < 0) {
          dx -= fx * along;
          dz -= fz * along;
        }
        const len = Math.hypot(dx, dz);
        if (len < 0.08) continue;
        player.body.applyImpulse({ x: (dx / len) * 18, y: 0, z: (dz / len) * 18 }, true);
        this.nudgeCooldown = 0.35;
        if (before > 6) this.bump();
      }
    }
    if (this.collisionCooldown <= 0 && before > 4) {
      let hitWall = false;
      this.world.contactPairsWith(player.collider, (other) => {
        const info = this.meta.get(other.handle);
        if (info?.kind === 'barrier' || info?.kind === 'obstacle') hitWall = true;
      });
      const after = Math.hypot(player.body.linvel().x, player.body.linvel().z);
      if (hitWall && before - after > 2.2) this.bump();
    }
  }

  private bump(): void {
    this.collisionCooldown = 0.45;
    this.race?.noteCollision();
    this.audio.collision();
    this.chase.addShake(0.7);
  }

  private async finishIfNeeded(): Promise<void> {
    if (this.finishLock || !this.race || !this.track || !this.phaseId || this.race.status !== 'finished') return;
    this.finishLock = true;
    const totalMs = this.race.finishTime * 1000;
    const update = await this.progress.recordFinish(this.phaseId, totalMs, this.race.bestLapMs, this.track.medals);
    this.ui.setProfile(this.progress.data, this.progress.kind, this.progress.degraded);
    if (this.track.finale === 'launch') {
      this.beginLaunch(update, totalMs);
      return;
    }
    this.presentResults(update);
  }

  private presentResults(update: FinishUpdate, totalOverride?: number, epilogue?: string): void {
    const phase = this.phaseId ? phaseById(this.phaseId) : null;
    const next = PHASES.find((entry) => entry.order === (phase?.order ?? 0) + 1);
    this.ui.showResults({
      ...update,
      totalMs: totalOverride ?? update.totalMs,
      phaseName: phase?.name ?? 'Fase',
      nextName: next?.name ?? null,
      epilogue: epilogue ?? null,
    });
    this.ui.setHudVisible(false);
    this.audio.beep(520, 0.18, 0.1);
    window.setTimeout(() => this.audio.beep(780, 0.24, 0.12), 160);
  }

  private updateHud(speed: number): void {
    const race = this.race;
    const track = this.track;
    const player = this.player;
    if (!race || !track || !player || !this.phaseId) return;
    const savedLap = this.progress.data.phases[this.phaseId].bestLapMs;
    const bestLap = race.bestLapMs == null ? savedLap : savedLap == null ? race.bestLapMs : Math.min(race.bestLapMs, savedLap);
    const score = race.rankingDistance(player.splineDistance);
    let position = 1;
    for (const racer of this.opponents?.racers ?? []) {
      if (racer.meters > score + 0.5) position += 1;
    }
    const grid = 1 + (this.opponents?.racers.length ?? 0);
    this.ui.updateHud({
      speed: formatSpeed(speed),
      lap: race.displayLap,
      totalLaps: race.totalLaps,
      raceMs: race.raceTime * 1000,
      lapMs: race.lapTime * 1000,
      bestLapMs: bestLap,
      bestRaceMs: this.progress.data.phases[this.phaseId].bestTimeMs,
      position,
      grid,
      progress: track.lapLength > 0 ? player.splineDistance / track.lapLength : 0,
      countdown: race.countdownLabel,
      message: this.launching ? this.launchMessage : race.message,
      collision: race.collisionFlash > 0,
      showHelp: this.helpTimer < 8 && speed < 2,
      boost: this.boost,
      boosting: this.boostWas,
      nitroFree: race.matchMode === 'nitroSpawnersOnly',
    });
  }

  private handleMenuKeys(): void {
    if (this.input.consumePressed('Escape') || this.input.gamepadPauseEdge) {
      if (this.mode === 'race' && this.race && this.race.status !== 'finished') {
        this.setPaused(!this.paused);
      } else if (this.mode === 'menu' && this.ui.current !== 'title' && this.ui.current !== 'loading') {
        this.ui.back();
      }
    }
    if (this.mode === 'race' && this.race?.status !== 'finished' && this.input.consumePressed('KeyR')) {
      this.respawn();
    }
    if (this.mode === 'race' && this.input.consumePressed('KeyC')) {
      const camera = this.progress.settings.camera === 'chase' ? 'high' : 'chase';
      void this.applySettings({ camera });
    }
    if (this.mode === 'race' && this.input.consumePressed('F3')) {
      this.setColliderDebug(this.colliderDebug == null);
    }
  }

  private followSun(target: THREE.Vector3): void {
    const direction = this.track?.atmosphere.sunDirection ?? new THREE.Vector3(-0.4, 1, 0.3);
    // Snap the shadow frustum to texel size so the map does not shimmer while turning.
    const texel = 44 / 1024;
    const sx = Math.round(target.x / texel) * texel;
    const sy = Math.round(target.y / texel) * texel;
    const sz = Math.round(target.z / texel) * texel;
    this.sun.position.set(sx, sy, sz).addScaledVector(direction, 28);
    this.sun.target.position.set(sx, sy, sz);
    this.sun.target.updateMatrixWorld();
  }

  private followSky(): void {
    const camera = this.chase.camera;
    this.track?.object.traverse((object) => {
      if (object.userData.followCamera) object.position.copy(camera.position);
    });
  }

  private frameMenuCamera(): void {
    const narrow = window.innerWidth < 800;
    const target = narrow ? new THREE.Vector3(0, 0.7, 0) : new THREE.Vector3(1.2, 0.7, 0);
    this.menuCart.root.position.x = narrow ? 0 : 1.2;
    this.menuCamera.position.set(target.x + 0.15, 1.35, 3.55);
    this.menuCamera.lookAt(target);
  }

  private publishDebug(): void {
    if (!import.meta.env.DEV) return;
    window.__SKR = {
      ready: this.started || this.fatalMessage != null,
      getState: () => this.debugState(),
      startPhase: (id: string) => this.startPhase(id as PhaseId),
      debugSkipCountdown: () => this.race?.skipCountdown(),
      debugTeleport: (distance: number, lateral = 0) => this.debugTeleport(distance, lateral),
      debugFinish: (timeMs?: number) => this.debugFinish(timeMs),
      debugDriveLap: (maxSeconds?: number) => this.debugDriveLap(maxSeconds),
      debugProbeLine: (spacing?: number) => this.debugProbeLine(spacing),
      debugColliders: (enabled: boolean) => this.setColliderDebug(enabled),
      getGameConfig: () => this.gameConfig,
      unlockAdmin: (code: string) => this.unlockAdmin(code),
      setPublicMatchMode: (mode: PublicMatchMode) => this.setPublicMatchMode(mode),
      resetProgress: () => this.progress.reset().then(() => {
        this.ui.setProfile(this.progress.data, this.progress.kind, this.progress.degraded);
      }),
      getProfile: () => this.progress.data,
    };
    window.__SKR.ready = true;
  }

  private debugState(): SkrState {
    const player = this.player;
    const race = this.race;
    const drive = this.input.drive();
    const completed: Record<string, boolean> = {};
    const unlocked: Record<string, boolean> = {};
    const implemented: Record<string, boolean> = {};
    if (this.progress) {
      for (const phase of PHASES) {
        completed[phase.id] = this.progress.isCompleted(phase.id);
        unlocked[phase.id] = isUnlocked(phase, (id) => this.progress.isCompleted(id)) && phase.implemented;
        implemented[phase.id] = phase.implemented;
      }
    }
    return {
      ready: this.started || this.fatalMessage != null,
      frames: this.frames,
      webgl: this.webgl,
      fatal: this.fatalMessage,
      screen: this.mode === 'race' ? (this.paused ? 'paused' : this.ui.current === 'results' ? 'results' : 'racing') : this.ui.current,
      phaseId: this.phaseId,
      paused: this.paused,
      racing: this.mode === 'race' && race?.status === 'racing' && !this.paused,
      countdown: race?.countdownLabel ?? null,
      speedMs: player?.telemetry.forwardSpeed ?? 0,
      speedKmh: formatSpeed(player?.telemetry.forwardSpeed ?? 0),
      throttle: drive.throttle,
      brake: drive.brake,
      steer: drive.steer,
      handbrake: drive.handbrake,
      boost: this.boost,
      boosting: this.boostWas,
      matchMode: this.gameConfig.publicMatch.mode,
      checkpointDistances: this.track?.checkpoints.map((marker) => marker.distance) ?? [],
      admin: this.adminUnlocked,
      lap: race?.displayLap ?? 0,
      totalLaps: race?.totalLaps ?? 0,
      completedLaps: race?.completedLaps ?? 0,
      nextCheckpoint: race?.nextCheckpoint ?? 0,
      checkpointCount: this.track?.checkpoints.length ?? 0,
      raceTimeMs: (race?.raceTime ?? 0) * 1000,
      lapTimeMs: (race?.lapTime ?? 0) * 1000,
      bestLapMs: race?.bestLapMs ?? null,
      finished: race?.status === 'finished',
      offTrack: player ? !player.probe.onTrack : false,
      grounded: player?.probe.grounded ?? false,
      surface: player?.probe.surface.id ?? '',
      toi: player?.probe.toi ?? 0,
      message: race?.message ?? '',
      position: 0,
      lapLength: this.track?.lapLength ?? 0,
      width: this.track?.width ?? 0,
      x: player?.currentPosition.x ?? 0,
      y: player?.currentPosition.y ?? 0,
      z: player?.currentPosition.z ?? 0,
      lateral: this.playerLateral(),
      splineDistance: player?.splineDistance ?? 0,
      nearestOpponent: this.nearestOpponentGap(),
      carts: this.countCarts(),
      colliderDebug: this.colliderDebug != null,
      opponents: this.opponentDebug(),
      mass: player?.body.mass() ?? 0,
      quality: this.graphics(),
      persistence: this.progress?.kind ?? 'memory',
      completed,
      unlocked,
      implemented,
    };
  }

  private frameIndexAt(distance: number): number {
    const frames = this.track?.frames;
    const lap = this.track?.lapLength ?? 1;
    if (!frames?.length) return 0;
    const wrapped = ((distance % lap) + lap) % lap;
    let index = 0;
    for (let i = 0; i < frames.length; i += 1) {
      if (frames[i].distance <= wrapped) index = i;
      else break;
    }
    return index;
  }

  private playerLateral(): number {
    return this.playerLateralAt(this.player?.currentPosition ?? new THREE.Vector3());
  }

  private playerLateralAt(point: THREE.Vector3): number {
    const player = this.player;
    const track = this.track;
    if (!player || !track) return 0;
    // Measured from the hint vertex, not the closest point. The scripted line
    // and the off-track check were tuned to that lag; a true offset over-steers
    // into the city chicane.
    const frame = track.frames[player.splineHint];
    if (!frame) return 0;
    const dx = point.x - frame.position.x;
    const dy = point.y - frame.position.y;
    const dz = point.z - frame.position.z;
    return dx * frame.binormal.x + dy * frame.binormal.y + dz * frame.binormal.z;
  }

  private driveForFrame(input: DriveInput, dt: number): DriveInput {
    const racing = this.race?.status === 'racing';
    const wants = Boolean(racing && input.boost && this.boost > 0.4);
    if (wants) this.boost = Math.max(0, this.boost - BOOST_DRAIN_PER_SEC * dt);
    const boosting = wants && this.boost > 0;
    if (boosting && !this.boostWas) this.audio.beep(760, 0.05, 0.04);
    this.boostWas = boosting;
    return { ...input, boost: boosting };
  }

  private collectBoost(race: RaceSession): void {
    for (const grant of race.consumeBoostGrants()) {
      this.boost = grant.pad === 'big' ? BOOST_MAX : Math.min(BOOST_MAX, this.boost + grant.amount);
      this.audio.beep(grant.pad === 'big' ? 920 : 640, 0.07, 0.05);
    }
  }

  private paintPads(): void {
    const race = this.race;
    const track = this.track;
    if (!race || !track) return;
    for (const marker of track.checkpoints) {
      const materials = marker.padObject.userData.boostMaterials as THREE.MeshStandardMaterial[] | undefined;
      if (!materials) continue;
      const ready = race.padGlow(marker.index);
      const hot = marker.pad === 'big' ? 1.7 : 1.15;
      for (const material of materials) material.emissiveIntensity = ready ? hot : 0.08;
    }
  }

  private restoreAdmin(): void {
    try {
      const raw = localStorage.getItem(ADMIN_STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw) as { id?: string };
      if (data.id === 'local-admin' || (data.id != null && configuredAdminIds().includes(data.id))) {
        this.adminUnlocked = true;
        this.adminId = data.id;
      }
    } catch {
      /* ignore broken storage */
    }
  }

  private unlockAdmin(code: string): boolean {
    if (!isAdminCredential(code)) {
      this.ui.setMatchAdmin(false, this.gameConfig.publicMatch.mode, 'Código recusado.');
      return false;
    }
    const trimmed = code.trim();
    this.adminUnlocked = true;
    this.adminId = configuredAdminIds().includes(trimmed) ? trimmed : 'local-admin';
    try {
      localStorage.setItem(ADMIN_STORAGE_KEY, JSON.stringify({ id: this.adminId }));
    } catch {
      /* private mode still unlocks this session */
    }
    this.ui.setMatchAdmin(true, this.gameConfig.publicMatch.mode, '');
    return true;
  }

  private async setPublicMatchMode(mode: PublicMatchMode): Promise<GameConfigDocument> {
    if (!this.adminUnlocked || !this.gameConfigClient) {
      this.ui.setMatchAdmin(false, this.gameConfig.publicMatch.mode, 'Somente administradores alteram a partida pública.');
      return this.gameConfig;
    }
    try {
      this.gameConfig = await this.gameConfigClient.setPublicMatchMode(mode, this.adminId);
      this.race?.setMatchMode(this.gameConfig.publicMatch.mode);
      this.ui.setMatchAdmin(true, this.gameConfig.publicMatch.mode, '');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Falha ao atualizar a partida pública.';
      this.ui.setMatchAdmin(true, this.gameConfig.publicMatch.mode, message);
    }
    return this.gameConfig;
  }

  private debugTeleport(distance: number, lateral: number): void {
    if (!this.player || !this.track) return;
    const pose = sampleSpline(this.track.frames, distance, this.track.lapLength);
    const position = pose.position.clone().addScaledVector(pose.normal, CART.rideHeight).addScaledVector(pose.binormal, lateral);
    this.player.teleport(position, pose.quaternion);
    this.player.splineHint = this.frameIndexAt(pose.distance);
    this.player.splineDistance = pose.distance;
    this.chase.snap(position, pose.quaternion);
  }

  private async debugFinish(timeMs?: number): Promise<void> {
    if (!this.race || !this.track || !this.phaseId || this.finishLock) return;
    this.race.status = 'finished';
    const total = timeMs ?? this.race.raceTime * 1000;
    this.race.finishTime = total / 1000;
    this.finishLock = true;
    const update = await this.progress.recordFinish(this.phaseId, total, this.race.bestLapMs, this.track.medals);
    this.ui.setProfile(this.progress.data, this.progress.kind, this.progress.degraded);
    if (this.track.finale === 'launch') this.beginLaunch(update, total);
    else this.presentResults(update, total);
  }

  private debugDriveLap(maxSeconds = 90): {
    completed: boolean;
    unwrapped: number;
    lap: number;
    minSpeed: number;
    maxStall: number;
    stuckAt: number | null;
    trace: string[];
  } {
    const empty = { completed: false, unwrapped: 0, lap: 0, minSpeed: 0, maxStall: 0, stuckAt: null as number | null, trace: [] as string[] };
    const player = this.player;
    const track = this.track;
    const race = this.race;
    if (!player || !track || !race || this.mode !== 'race') return empty;
    race.skipCountdown();
    this.scriptedDrive = true;
    const dt = 1 / 60;
    this.world.timestep = dt;
    const lap = track.lapLength;
    const limit = Math.min(Math.max(8, maxSeconds), lap / 3.2 + 18);
    const steps = Math.ceil(limit / dt);
    let unwrapped = 0;
    let prev = player.splineDistance;
    let minSpeed = Infinity;
    let stall = 0;
    let maxStall = 0;
    let stuckAt: number | null = null;
    let warmed = 0;
    const trace: string[] = [];
    const forward = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    for (let step = 0; step < steps; step += 1) {
      const body = player.body;
      const translation = body.translation();
      const ahead = sampleSpline(track.frames, player.splineDistance + 7, lap);
      const dx = ahead.position.x - translation.x;
      const dz = ahead.position.z - translation.z;
      const desiredLen = Math.hypot(dx, dz) || 1;
      const rotation = body.rotation();
      quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
      forward.set(0, 0, 1).applyQuaternion(quaternion);
      const forwardLen = Math.hypot(forward.x, forward.z) || 1;
      const fx = forward.x / forwardLen;
      const fz = forward.z / forwardLen;
      const cross = fx * (dz / desiredLen) - fz * (dx / desiredLen);
      const dot = fx * (dx / desiredLen) + fz * (dz / desiredLen);
      const heading = Math.atan2(cross, dot);
      const lateral = this.playerLateral();
      const steer = THREE.MathUtils.clamp(-heading * 1.7 - lateral * 0.42, -1, 1);
      this.fixedStep(dt, { throttle: 1, brake: 0, steer, handbrake: false, boost: false });
      const speed = Math.abs(player.telemetry.forwardSpeed);
      const now = player.splineDistance;
      let delta = now - prev;
      if (delta < -lap * 0.5) delta += lap;
      else if (delta > lap * 0.5) delta -= lap;
      if (delta > 0) unwrapped += delta;
      warmed += dt;
      if (warmed > 1.2) minSpeed = Math.min(minSpeed, speed);
      const stalled = warmed > 1.2 && speed < 0.6 && delta < 0.025;
      if (stalled) {
        stall += dt;
        if (stall > maxStall) {
          maxStall = stall;
          stuckAt = now;
        }
      } else {
        stall = 0;
      }
      prev = now;
      if (step % 90 === 0 || (stalled && stall > 0.4 && trace.length < 12)) {
        const at = player.body.translation();
        trace.push(`${now.toFixed(0)} u${unwrapped.toFixed(0)} v${speed.toFixed(1)} lat${this.playerLateral().toFixed(1)} ${at.x.toFixed(0)},${at.z.toFixed(0)}`);
      }
      if (stall > 1.25) break;
      if (unwrapped >= lap * 0.98) break;
    }
    const min = Number.isFinite(minSpeed) ? minSpeed : 0;
    const completed = unwrapped >= lap * 0.98 && maxStall <= 1.25;
    this.scriptedDrive = false;
    return { completed, unwrapped, lap, minSpeed: min, maxStall, stuckAt: completed ? null : stuckAt, trace };
  }

  private debugProbeLine(spacing = 18): {
    stuck: Array<{ distance: number; speed: number; progress: number; lateral: number; kind: string; hits: string }>;
    samples: number;
  } {
    const stuck: Array<{ distance: number; speed: number; progress: number; lateral: number; kind: string; hits: string }> = [];
    const track = this.track;
    const player = this.player;
    const race = this.race;
    if (!track || !player || !race || this.mode !== 'race') return { stuck, samples: 0 };
    race.skipCountdown();
    this.scriptedDrive = true;
    const dt = 1 / 60;
    this.world.timestep = dt;
    const lap = track.lapLength;
    let samples = 0;
    for (let distance = 4; distance < lap - 4; distance += spacing) {
      samples += 1;
      this.debugTeleport(distance, 0);
      for (let step = 0; step < 90; step += 1) {
        const translation = player.body.translation();
        const ahead = sampleSpline(track.frames, player.splineDistance + 7, lap);
        const dx = ahead.position.x - translation.x;
        const dz = ahead.position.z - translation.z;
        const desiredLen = Math.hypot(dx, dz) || 1;
        const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(player.currentQuaternion);
        const forwardLen = Math.hypot(forward.x, forward.z) || 1;
        const nx = forward.x / forwardLen;
        const nz = forward.z / forwardLen;
        const cross = nx * (dz / desiredLen) - nz * (dx / desiredLen);
        const steer = THREE.MathUtils.clamp(-Math.atan2(cross, Math.max(0.2, nx * (dx / desiredLen) + nz * (dz / desiredLen))) * 1.7 - this.playerLateral() * 0.42, -1, 1);
        this.fixedStep(dt, { throttle: 1, brake: 0, steer, handbrake: false, boost: false });
      }
      const speed = Math.abs(player.telemetry.forwardSpeed);
      const travel = Math.hypot(
        player.currentPosition.x - sampleSpline(track.frames, distance, lap).position.x,
        player.currentPosition.y - sampleSpline(track.frames, distance, lap).position.y,
        player.currentPosition.z - sampleSpline(track.frames, distance, lap).position.z,
      );
      if (speed < 1.5 && travel < 3) {
        const hits = new Set<string>();
        this.world.contactPairsWith(player.collider, (other) => {
          const kind = this.meta.get(other.handle)?.kind;
          if (kind) hits.add(kind);
        });
        stuck.push({
          distance: Math.round(distance),
          speed: Number(speed.toFixed(2)),
          progress: Number(travel.toFixed(2)),
          lateral: Number(this.playerLateral().toFixed(2)),
          kind: player.probe.kind,
          hits: [...hits].join(',') || 'none',
        });
      }
    }
    this.scriptedDrive = false;
    return { stuck, samples };
  }

  private setColliderDebug(enabled: boolean): void {
    this.clearColliderDebug();
    if (!enabled || !this.track || !this.raceScene) return;
    const group = new THREE.Group();
    group.name = 'collider-debug';
    const paint = (collider: RAPIER.Collider, color: number, opacity: number) => {
      const mesh = debugColliderMesh(collider, color, opacity);
      if (mesh) group.add(mesh);
    };
    for (const collider of this.trackColliders) {
      const kind = this.meta.get(collider.handle)?.kind ?? 'ground';
      if (kind === 'ground') continue;
      const color = kind === 'road' ? 0x3dff7a : kind === 'barrier' ? 0xff3355 : kind === 'obstacle' ? 0xff9a1f : 0x66ccff;
      paint(collider, color, kind === 'checkpoint' ? 0.22 : 0.55);
    }
    if (this.player) paint(this.player.collider, 0xffffff, 0.9);
    for (const racer of this.opponents?.racers ?? []) paint(racer.collider, 0xff66cc, 0.9);
    this.raceScene.add(group);
    this.colliderDebug = group;
  }

  private clearColliderDebug(): void {
    if (!this.colliderDebug) return;
    this.colliderDebug.removeFromParent();
    disposeObject(this.colliderDebug);
    this.colliderDebug = null;
  }

  private countCarts(): number {
    if (!this.raceScene) return 0;
    let count = 0;
    this.raceScene.traverse((object) => {
      if (object.name === 'cart') count += 1;
    });
    return count;
  }

  private nearestOpponentGap(): number {
    const player = this.player;
    if (!player || !this.opponents?.racers.length) return Number.POSITIVE_INFINITY;
    const origin = player.body.translation();
    let best = Number.POSITIVE_INFINITY;
    for (const racer of this.opponents.racers) {
      const ai = racer.body.translation();
      best = Math.min(best, Math.hypot(origin.x - ai.x, origin.z - ai.z));
    }
    return best;
  }

  private opponentDebug(): Array<{ meters: number; lane: number; x: number; z: number }> {
    return (this.opponents?.racers ?? []).map((racer) => {
      const at = racer.body.translation();
      return { meters: racer.meters, lane: racer.lane, x: at.x, z: at.z };
    });
  }

  private beginLaunch(update: FinishUpdate, totalMs?: number): void {
    this.launching = true;
    this.launchTimer = 6.4;
    this.launchElapsed = 0;
    this.launchBeepStep = -1;
    this.launchUpdate = update;
    this.launchTotal = totalMs;
    this.launchMessage = 'Torre: carrinho autorizado na plataforma.';
    this.ui.setHudVisible(true);
  }

  private updateLaunch(dt: number): void {
    this.launchElapsed += dt;
    this.launchTimer -= dt;
    const focus = this.track?.finaleFocus;
    if (focus) {
      const camera = this.chase.camera;
      const desired = focus.clone().add(new THREE.Vector3(16, 7, 18));
      camera.position.lerp(desired, 1 - Math.exp(-1.4 * dt));
      camera.lookAt(focus);
    }
    const cues = [
      { at: 0, text: 'Torre: carrinho autorizado na plataforma.', beep: 0 },
      { at: 1.3, text: 'Contagem 3', beep: 520 },
      { at: 2.5, text: 'Contagem 2', beep: 520 },
      { at: 3.7, text: 'Contagem 1', beep: 520 },
      { at: 4.9, text: 'Ignição. O módulo de propulsão sobe com o foguete.', beep: 180 },
    ];
    let step = 0;
    for (let index = 0; index < cues.length; index += 1) {
      if (this.launchElapsed >= cues[index].at) step = index;
    }
    if (step !== this.launchBeepStep) {
      this.launchBeepStep = step;
      this.launchMessage = cues[step].text;
      if (cues[step].beep > 0) this.audio.beep(cues[step].beep, cues[step].beep < 300 ? 0.4 : 0.12, 0.1);
    }
    if (this.launchTimer <= 0 && this.launchUpdate) {
      const update = this.launchUpdate;
      const total = this.launchTotal;
      this.launching = false;
      this.launchUpdate = null;
      this.presentResults(
        update,
        total,
        'A ignição libera o módulo de propulsão. A próxima fase é o Espaço: a estrada continua em órbita.',
      );
    }
  }

  private clearMotes(): void {
    if (!this.motes) return;
    this.raceScene?.remove(this.motes);
    this.motes.geometry.dispose();
    const material = this.motes.material;
    if (!Array.isArray(material)) material.dispose();
    this.motes = null;
    this.moteSpeeds = null;
  }

  private spawnMotes(): void {
    this.clearMotes();
    const count = this.graphics() === 'high' ? 64 : 22;
    const positions = new Float32Array(count * 3);
    this.moteSpeeds = new Float32Array(count);
    for (let index = 0; index < count; index += 1) {
      positions[index * 3] = (Math.random() - 0.5) * 16;
      positions[index * 3 + 1] = Math.random() * 6;
      positions[index * 3 + 2] = (Math.random() - 0.5) * 16;
      this.moteSpeeds[index] = 0.7 + Math.random();
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.motes = new THREE.Points(geometry, new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.16,
      transparent: true,
      opacity: 0.72,
      depthWrite: false,
    }));
    this.motes.frustumCulled = false;
    this.motes.visible = false;
    this.raceScene.add(this.motes);
  }

  private updateMotes(speed: number, dt: number): void {
    if (!this.motes || !this.player || !this.track || !this.moteSpeeds) return;
    const kind = this.track.ambience ?? 'none';
    const snow = kind === 'snow';
    const dust = kind === 'desert' || kind === 'dirt' || this.player.probe.surface.dust;
    const active = snow || (dust && Math.abs(speed) > 2.5);
    this.motes.visible = active;
    if (!active) return;
    const attr = this.motes.geometry.getAttribute('position') as THREE.BufferAttribute;
    const material = this.motes.material as THREE.PointsMaterial;
    material.color.set(snow ? 0xf7fbff : kind === 'dirt' ? 0x8d5a32 : 0xe6d2a4);
    const anchor = snow ? this.chase.camera.position : this.player.view.root.position;
    const back = new THREE.Vector3(0, 0, -1).applyQuaternion(this.player.view.root.quaternion);
    for (let index = 0; index < attr.count; index += 1) {
      const fall = attr.getY(index) - this.moteSpeeds[index] * dt * (snow ? 2.4 : 0.35);
      if (snow && fall < anchor.y - 1.5) {
        attr.setXYZ(
          index,
          anchor.x + (Math.random() - 0.5) * 22,
          anchor.y + 5 + Math.random() * 4,
          anchor.z + (Math.random() - 0.5) * 22,
        );
      } else if (!snow && Math.random() < 0.4) {
        attr.setXYZ(
          index,
          anchor.x + back.x * (0.5 + Math.random()) + (Math.random() - 0.5) * 0.7,
          anchor.y + 0.12 + Math.random() * 0.35,
          anchor.z + back.z * (0.5 + Math.random()) + (Math.random() - 0.5) * 0.7,
        );
      } else {
        attr.setY(index, fall);
      }
    }
    attr.needsUpdate = true;
  }
}

function debugColliderMesh(collider: RAPIER.Collider, color: number, opacity: number): THREE.Mesh | null {
  const translation = collider.translation();
  const rotation = collider.rotation();
  const x = translation.x;
  const y = translation.y;
  const z = translation.z;
  const qx = rotation.x;
  const qy = rotation.y;
  const qz = rotation.z;
  const qw = rotation.w;
  const type = collider.shapeType();
  let geometry: THREE.BufferGeometry | null = null;
  if (type === RAPIER.ShapeType.Cuboid) {
    const extents = collider.halfExtents();
    geometry = new THREE.BoxGeometry(extents.x * 2, extents.y * 2, extents.z * 2);
  } else if (type === RAPIER.ShapeType.Ball) {
    geometry = new THREE.SphereGeometry(collider.radius(), 12, 8);
  }
  if (!geometry) return null;
  const mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({
    color,
    wireframe: true,
    transparent: true,
    opacity,
    depthTest: false,
  }));
  mesh.position.set(x, y, z);
  mesh.quaternion.set(qx, qy, qz, qw);
  mesh.frustumCulled = false;
  mesh.renderOrder = 10;
  return mesh;
}

interface SkrState {
  ready: boolean;
  frames: number;
  webgl: boolean;
  fatal: string | null;
  screen: string;
  phaseId: string | null;
  paused: boolean;
  racing: boolean;
  countdown: string | null;
  speedMs: number;
  speedKmh: number;
  throttle: number;
  brake: number;
  steer: number;
  handbrake: boolean;
  boost: number;
  boosting: boolean;
  matchMode: string;
  checkpointDistances: number[];
  admin: boolean;
  lap: number;
  totalLaps: number;
  completedLaps: number;
  nextCheckpoint: number;
  checkpointCount: number;
  raceTimeMs: number;
  lapTimeMs: number;
  bestLapMs: number | null;
  finished: boolean;
  offTrack: boolean;
  grounded: boolean;
  surface: string;
  toi: number;
  message: string;
  position: number;
  lapLength: number;
  width: number;
  x: number;
  y: number;
  z: number;
  lateral: number;
  splineDistance: number;
  nearestOpponent: number;
  carts: number;
  colliderDebug: boolean;
  opponents: Array<{ meters: number; lane: number; x: number; z: number }>;
  mass: number;
  quality: 'low' | 'high';
  persistence: string;
  completed: Record<string, boolean>;
  unlocked: Record<string, boolean>;
  implemented: Record<string, boolean>;
}

declare global {
  interface Window {
    __SKR?: {
      ready: boolean;
      getState: () => SkrState;
      startPhase: (id: string) => Promise<void>;
      debugSkipCountdown: () => void;
      debugTeleport: (distance: number, lateral?: number) => void;
      debugFinish: (timeMs?: number) => Promise<void>;
      debugDriveLap: (maxSeconds?: number) => { completed: boolean; unwrapped: number; lap: number; minSpeed: number; maxStall: number; stuckAt: number | null; trace: string[] };
      debugProbeLine: (spacing?: number) => {
        stuck: Array<{ distance: number; speed: number; progress: number; lateral: number; kind: string; hits: string }>;
        samples: number;
      };
      debugColliders: (enabled: boolean) => void;
      getGameConfig: () => GameConfigDocument;
      unlockAdmin: (code: string) => boolean;
      setPublicMatchMode: (mode: PublicMatchMode) => Promise<GameConfigDocument>;
      resetProgress: () => Promise<void>;
      getProfile: () => unknown;
    };
  }
}
